import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Button, Input } from '@follow/ui';
import { XMarkIcon } from '@heroicons/react/24/outline';

import UserService from '../../services/UserService';

interface AccountSettingsProps {
  open: boolean;
  onClose: () => void;
}

const AccountSettings: React.FC<AccountSettingsProps> = ({ open, onClose }) => {
  const queryClient = useQueryClient();

  const { data: user } = useQuery({
    queryKey: ['user'],
    queryFn: () => UserService.getUser(),
    select: (data) => data.data,
  });

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const prevOpen = useRef(false);

  useEffect(() => {
    if (open && !prevOpen.current && user) {
      setFirstName(user.firstName);
      setLastName(user.lastName);
      setEmail(user.email);
      setPhone(user.phone ?? '');
      setIsDirty(false);
    }
    prevOpen.current = open;
  }, [open, user]);

  const resetForm = () => {
    if (user) {
      setFirstName(user.firstName);
      setLastName(user.lastName);
      setEmail(user.email);
      setPhone(user.phone ?? '');
      setIsDirty(false);
    }
  };

  const handleChange = (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setter(e.target.value);
    setIsDirty(true);
  };

  const handleClose = () => {
    if (isDirty) resetForm();
    onClose();
  };

  const updateUserMutation = useMutation({
    mutationFn: (data: { firstName: string; lastName: string; phone: string }) =>
      UserService.updateUser(data),
    onSuccess: () => {
      toast.success('Compte mis à jour');
      setIsDirty(false);
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
    onError: () => {
      toast.error('Erreur lors de la mise à jour');
    },
  });

  const handleSave = () => {
    updateUserMutation.mutate({ firstName, lastName, phone });
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onMouseDown={handleClose}
    >
      <div
        className="w-full max-w-[640px] max-h-[90vh] overflow-y-auto bg-white rounded-3xl shadow-xl mx-4"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 pb-0">
          <h1 className="text-neutral typo-h1">Paramètres du compte</h1>
          <button
            type="button"
            className="p-1 rounded-lg hover:bg-neutral-5 transition-colors text-neutral-60"
            onClick={handleClose}
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 flex flex-col gap-4">
          <h2 className="text-neutral typo-h2">Détails</h2>
          <div className="flex flex-col gap-6">
            <div className="w-full max-w-[480px] flex flex-col gap-1">
              <label className="text-neutral-80 typo-label">Prénom</label>
              <Input
                tone="dark"
                type="text"
                placeholder="Prénom"
                value={firstName}
                onChange={handleChange(setFirstName)}
              />
            </div>
            <div className="w-full max-w-[480px] flex flex-col gap-1">
              <label className="text-neutral-80 typo-label">Nom</label>
              <Input
                tone="dark"
                type="text"
                placeholder="Nom"
                value={lastName}
                onChange={handleChange(setLastName)}
              />
            </div>
            <div className="w-full max-w-[480px] flex flex-col gap-1">
              <label className="text-neutral-80 typo-label">Email</label>
              <Input
                tone="dark"
                type="email"
                placeholder="Email"
                value={email}
                disabled
              />
            </div>
            <div className="w-full max-w-[480px] flex flex-col gap-1">
              <label className="text-neutral-80 typo-label">Téléphone</label>
              <Input
                tone="dark"
                type="tel"
                placeholder="Téléphone"
                value={phone}
                onChange={handleChange(setPhone)}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={handleClose}>
                Annuler
              </Button>
              <Button variant="primary" tone="light" size="sm" onClick={handleSave} disabled={!isDirty}>
                Sauvegarder
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default AccountSettings;
